import * as THREE from 'three';

export type NetworkHealthStatus = 'healthy' | 'congested' | 'critical';

export interface BitcoinSunConfig {
  radius: number;
  baseColor: THREE.Color;
  glowColor: THREE.Color;
  pulsation: {
    baseScale: number;
    amplitude: number;
    frequency: number;
    activityMultiplier: number;
  };
  surface: {
    turbulence: number;
    noiseScale: number;
    animationSpeed: number;
  };
}

export interface CoronaConfig {
  particles: {
    count: number;
    baseSize: number;
    sizeVariation: number;
    speed: number;
    turbulence: number;
  };
  flares: {
    frequency: number;
    intensity: number;
    duration: number;
    maxHeight: number;
  };
}

export class BitcoinSun extends THREE.Group {
  private config: BitcoinSunConfig;
  private coronaConfig: CoronaConfig;
  
  // Core sun components
  private sunMesh: THREE.Mesh;
  private coronaSystem: THREE.Points;
  private glowMesh: THREE.Mesh;
  
  // Animation state
  private time: number = 0;
  private activityLevel: number = 0;
  private healthStatus: NetworkHealthStatus = 'healthy';
  private pulsationPhase: number = 0;
  
  // Materials
  private sunMaterial: THREE.ShaderMaterial;
  private coronaMaterial: THREE.PointsMaterial;
  private glowMaterial: THREE.ShaderMaterial;
  
  constructor(config?: Partial<BitcoinSunConfig>, coronaConfig?: Partial<CoronaConfig>) {
    super();
    
    // Initialize userData early
    this.userData = { id: 'bitcoin-sun', type: 'sun' };
    
    this.config = {
      radius: 10,
      baseColor: new THREE.Color(0xf7931a), // Bitcoin orange
      glowColor: new THREE.Color(0xffffff),
      pulsation: {
        baseScale: 1.0,
        amplitude: 0.1,
        frequency: 0.5, // Hz
        activityMultiplier: 2.0,
      },
      surface: {
        turbulence: 0.5,
        noiseScale: 1.0,
        animationSpeed: 0.1,
      },
      ...config,
    };
    
    this.coronaConfig = {
      particles: {
        count: 2000,
        baseSize: 0.5,
        sizeVariation: 0.3,
        speed: 1.0,
        turbulence: 0.8,
      },
      flares: {
        frequency: 0.1, // per second
        intensity: 1.0,
        duration: 3.0,
        maxHeight: 5.0,
      },
      ...coronaConfig,
    };
    
    this.createSunMaterials();
    this.createSunCore();
    this.createGlow();
    this.createCoronaSystem();
  }
  
  private createSunMaterials(): void {
    // Sun core material with enhanced shader for rim lighting
    this.sunMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0.0 },
        baseColor: { value: this.config.baseColor },
        activityLevel: { value: 0.0 },
        turbulence: { value: this.config.surface.turbulence },
        noiseScale: { value: this.config.surface.noiseScale },
        healthStatus: { value: 0.0 }, // 0=healthy, 1=congested, 2=critical
        rimIntensity: { value: 0.3 }, // Enhanced rim lighting
        colorTemperature: { value: 0.0 }, // 0=cool, 1=warm
      },
      vertexShader: this.getSunVertexShader(),
      fragmentShader: this.getSunFragmentShader(),
      transparent: false,
    });
    
    // Enhanced atmospheric glow material with activity response
    this.glowMaterial = new THREE.ShaderMaterial({
      uniforms: {
        glowColor: { value: this.config.glowColor },
        intensity: { value: 1.0 },
        activityBoost: { value: 0.0 },
        atmosphericDensity: { value: 0.5 }, // Controls atmospheric thickness
        pulsePhase: { value: 0.0 }, // For breathing effect
        networkActivity: { value: 0.0 }, // Network activity level
        time: { value: 0.0 },
      },
      vertexShader: this.getGlowVertexShader(),
      fragmentShader: this.getGlowFragmentShader(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
    });
    
    // Remove corona material as we're no longer using particles
    this.coronaMaterial = null;
  }
  
  private createSunCore(): void {
    const geometry = new THREE.SphereGeometry(this.config.radius, 64, 32);
    this.sunMesh = new THREE.Mesh(geometry, this.sunMaterial);
    this.add(this.sunMesh);
  }
  
  private createGlow(): void {
    // Create multiple glow layers for atmospheric effect
    const baseGlowRadius = this.config.radius * 1.3;
    const atmosphericGlowRadius = this.config.radius * 2.0;
    
    // Base glow (close to surface)
    const glowGeometry = new THREE.SphereGeometry(baseGlowRadius, 32, 16);
    this.glowMesh = new THREE.Mesh(glowGeometry, this.glowMaterial);
    this.add(this.glowMesh);
    
    // Atmospheric glow (extended atmosphere)
    const atmosphericGeometry = new THREE.SphereGeometry(atmosphericGlowRadius, 24, 12);
    const atmosphericMaterial = this.glowMaterial.clone();
    atmosphericMaterial.uniforms['intensity'].value = 0.3;
    atmosphericMaterial.uniforms['atmosphericDensity'].value = 0.8;
    
    const atmosphericGlow = new THREE.Mesh(atmosphericGeometry, atmosphericMaterial);
    atmosphericGlow.userData = { id: 'atmospheric-glow', type: 'atmosphere' };
    this.add(atmosphericGlow);
    
    // Store reference for updates
    this.userData['atmosphericGlow'] = atmosphericGlow;
  }
  
  private createCoronaSystem(): void {
    // Corona system has been replaced with atmospheric glow
    // The atmospheric effect is now handled in the glow material and rim lighting
    // This eliminates the visual noise while maintaining network activity indication
  }
  
  public updateActivity(txPerSecond: number): void {
    // Normalize transaction rate to activity level (0-1)
    // Assume 100 tx/s as high activity
    this.activityLevel = Math.min(txPerSecond / 100.0, 1.0);
    
    // Update sun material uniforms
    this.sunMaterial.uniforms['activityLevel'].value = this.activityLevel;
    this.sunMaterial.uniforms['colorTemperature'].value = this.activityLevel;
    this.sunMaterial.uniforms['rimIntensity'].value = 0.3 + this.activityLevel * 0.4;
    
    // Update glow material uniforms
    this.glowMaterial.uniforms['activityBoost'].value = this.activityLevel;
    this.glowMaterial.uniforms['networkActivity'].value = this.activityLevel;
    this.glowMaterial.uniforms['atmosphericDensity'].value = 0.5 + this.activityLevel * 0.3;
    
    // Update atmospheric glow if it exists
    const atmosphericGlow = this.userData['atmosphericGlow'];
    if (atmosphericGlow && atmosphericGlow.material) {
      atmosphericGlow.material.uniforms['networkActivity'].value = this.activityLevel;
      atmosphericGlow.material.uniforms['intensity'].value = 0.3 + this.activityLevel * 0.2;
    }
  }
  
  public pulse(intensity: number, duration: number): void {
    // Trigger a pulse animation
    const startTime = this.time;
    const animate = () => {
      const elapsed = this.time - startTime;
      if (elapsed < duration) {
        const progress = elapsed / duration;
        const pulseValue = Math.sin(progress * Math.PI) * intensity;
        
        this.scale.setScalar(1.0 + pulseValue * 0.2);
        this.glowMaterial.uniforms['intensity'].value = 1.0 + pulseValue * 0.5;
        
        requestAnimationFrame(animate);
      } else {
        // Reset to normal
        this.scale.setScalar(1.0);
        this.glowMaterial.uniforms['intensity'].value = 1.0;
      }
    };
    animate();
  }
  
  public setHealthStatus(status: NetworkHealthStatus): void {
    this.healthStatus = status;
    
    let healthValue = 0.0;
    switch (status) {
      case 'healthy':
        healthValue = 0.0;
        break;
      case 'congested':
        healthValue = 1.0;
        break;
      case 'critical':
        healthValue = 2.0;
        break;
    }
    
    this.sunMaterial.uniforms['healthStatus'].value = healthValue;
  }
  
  public getScale(): number {
    return this.sunMesh.scale.x;
  }
  
  public getCurrentColor(): THREE.Color {
    return this.sunMaterial.uniforms['baseColor'].value.clone();
  }
  
  public isPulsing(): boolean {
    return this.scale.x !== 1.0;
  }
  
  public update(deltaTime: number): void {
    this.time += deltaTime;
    
    // Update base pulsation
    this.pulsationPhase += deltaTime * this.config.pulsation.frequency * 2 * Math.PI;
    const basePulsation = Math.sin(this.pulsationPhase) * this.config.pulsation.amplitude;
    const activityPulsation = this.activityLevel * this.config.pulsation.activityMultiplier;
    const totalScale = this.config.pulsation.baseScale + basePulsation + activityPulsation * 0.05; // Reduced for subtlety
    
    // Apply subtle pulsation if not manually pulsing
    if (this.scale.x === 1.0) {
      this.sunMesh.scale.setScalar(totalScale);
    }
    
    // Update shader time uniforms
    this.sunMaterial.uniforms['time'].value = this.time * this.config.surface.animationSpeed;
    this.glowMaterial.uniforms['time'].value = this.time;
    this.glowMaterial.uniforms['pulsePhase'].value = this.pulsationPhase;
    
    // Update atmospheric glow time
    const atmosphericGlow = this.userData['atmosphericGlow'];
    if (atmosphericGlow && atmosphericGlow.material) {
      atmosphericGlow.material.uniforms['time'].value = this.time;
      atmosphericGlow.material.uniforms['pulsePhase'].value = this.pulsationPhase * 0.7; // Slightly offset
    }
    
    // No longer need to update corona particles
  }
  
  private updateCoronaParticles(_deltaTime: number): void {
    // Corona particles have been replaced with atmospheric glow
    // No particle updates needed - effect is now shader-based
  }
  
  private getSunVertexShader(): string {
    return `
      uniform float time;
      uniform float turbulence;
      uniform float noiseScale;
      uniform float activityLevel;
      
      varying vec3 vPosition;
      varying vec3 vNormal;
      varying vec2 vUv;
      
      // Simple noise function
      float noise(vec3 p) {
        return sin(p.x * 10.0) * sin(p.y * 10.0) * sin(p.z * 10.0) * 0.1;
      }
      
      void main() {
        vUv = uv;
        vNormal = normalize(normal);
        
        // Calculate surface displacement
        vec3 pos = position;
        vec3 noisePos = pos * noiseScale + time;
        float displacement = noise(noisePos) * turbulence * (1.0 + activityLevel);
        
        // Apply displacement along normal
        pos += normal * displacement;
        
        vPosition = pos;
        
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `;
  }
  
  private getSunFragmentShader(): string {
    return `
      uniform vec3 baseColor;
      uniform float time;
      uniform float activityLevel;
      uniform float healthStatus;
      uniform float rimIntensity;
      uniform float colorTemperature;
      
      varying vec3 vPosition;
      varying vec3 vNormal;
      varying vec2 vUv;
      
      void main() {
        // Base color with health status and temperature modification
        vec3 color = baseColor;
        
        // Color temperature shift based on network activity
        float warmth = colorTemperature * activityLevel;
        color = mix(color, vec3(1.0, 0.6, 0.2), warmth * 0.3);
        
        if (healthStatus > 0.5) {
          // Congested - shift toward red
          float redShift = (healthStatus - 0.5) * 2.0;
          color = mix(color, vec3(1.0, 0.3, 0.1), redShift * 0.5);
        }
        
        // Enhanced surface details with noise
        float noise = sin(vPosition.x * 20.0 + time) * 
                     sin(vPosition.y * 20.0 + time) * 
                     sin(vPosition.z * 20.0 + time) * 0.1;
        
        // Enhanced rim lighting that responds to activity
        vec3 viewDir = normalize(-vPosition);
        float rim = 1.0 - max(0.0, dot(viewDir, vNormal));
        rim = pow(rim, 1.5);
        
        // Activity-enhanced rim intensity
        float dynamicRimIntensity = rimIntensity + (activityLevel * 0.4);
        vec3 rimColor = mix(baseColor, vec3(1.0, 0.8, 0.4), activityLevel);
        
        // Activity-based brightness with subtle pulsing
        float brightness = 1.0 + activityLevel * 0.3 + noise;
        float pulse = 1.0 + sin(time * 2.0) * activityLevel * 0.1;
        brightness *= pulse;
        
        // Combine effects
        color *= brightness;
        color += rim * rimColor * dynamicRimIntensity;
        
        gl_FragColor = vec4(color, 1.0);
      }
    `;
  }
  
  private getGlowVertexShader(): string {
    return `
      varying vec3 vNormal;
      
      void main() {
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
  }
  
  private getGlowFragmentShader(): string {
    return `
      uniform vec3 glowColor;
      uniform float intensity;
      uniform float activityBoost;
      uniform float atmosphericDensity;
      uniform float pulsePhase;
      uniform float networkActivity;
      uniform float time;
      
      varying vec3 vNormal;
      
      void main() {
        // Base glow calculation
        float glow = dot(vNormal, vec3(0.0, 0.0, 1.0));
        glow = 1.0 - glow;
        
        // Enhanced atmospheric falloff
        float atmosphericGlow = pow(glow, 2.0 + atmosphericDensity);
        
        // Activity-based pulsing
        float pulse = 1.0 + sin(time * 3.0 + pulsePhase) * networkActivity * 0.2;
        
        // Dynamic intensity based on network activity
        float totalIntensity = intensity + activityBoost * 0.6;
        totalIntensity *= pulse;
        
        // Color shifts based on activity
        vec3 dynamicGlowColor = mix(
          glowColor, 
          vec3(1.0, 0.7, 0.3), // Warmer color when active
          networkActivity * 0.4
        );
        
        // Atmospheric layering effect
        float atmosphericAlpha = atmosphericGlow * totalIntensity * 0.6;
        
        // Add subtle noise for atmospheric turbulence
        float noise = sin(time + gl_FragCoord.x * 0.01) * cos(time + gl_FragCoord.y * 0.01);
        atmosphericAlpha += noise * networkActivity * 0.05;
        
        gl_FragColor = vec4(dynamicGlowColor * atmosphericGlow * totalIntensity, atmosphericAlpha);
      }
    `;
  }
  
  public dispose(): void {
    // Dispose geometries
    this.sunMesh.geometry.dispose();
    this.glowMesh.geometry.dispose();
    
    // Dispose atmospheric glow if it exists
    const atmosphericGlow = this.userData['atmosphericGlow'];
    if (atmosphericGlow) {
      atmosphericGlow.geometry.dispose();
      if (atmosphericGlow.material) {
        atmosphericGlow.material.dispose();
      }
    }
    
    // Dispose materials
    this.sunMaterial.dispose();
    this.glowMaterial.dispose();
    
    // Corona material no longer exists
  }
}